// Content showcase behaviours:
//  1) Spread (all viewports, animated instances): the arched image trio is driven by the
//     section's transit through the viewport. --cs-spread (0 = sides stacked flat behind the
//     middle / centred, 1 = full arch) is written each frame by a single rAF-batched scroll
//     handler; CSS interpolates each card's transform from that one scalar.
//       · Desktop — SYMMETRIC: 0% & 100% of the transit centred, 50% widest. Reverses on the
//         way out (scroll back down → runs backwards to centred).
//       · Mobile — MONOTONIC: rises then HOLDS at widest (no collapse). Once fully spread the
//         trio hands off to the coverflow below; the horizontal swipe is only allowed after
//         the spread animation has finished.
//  2) Mobile: after hand-off the trio is a full-bleed COVERFLOW carousel (.cs-live) — JS drives
//     each card's rotate/drop from its distance to screen-centre so images ride the arch as you
//     swipe (centre card flat & upright). The row is NON-scrollable until hand-off, so there's
//     no scroll position to drift on hand-off or be restored off-centre on refresh.
//  Transform-only, no layout thrash mid-frame, honours prefers-reduced-motion — these are the
//  anti-flicker rules this section was rebuilt around.
(() => {
  const medias = [...document.querySelectorAll('.cs__media')];
  if (!medias.length) return;

  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MAX_ROT = 10; // deg at one slot from centre (coverflow)
  const DROP = 22;    // px at one slot from centre (coverflow)
  const PEAK = 0.5;   // transit progress at which the spread reaches its widest

  const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

  const centreMiddle = (media) => {
    const mid = media.querySelector('.cs__card--mid');
    if (!mid) return;
    media.scrollLeft = mid.offsetLeft - (media.clientWidth - mid.offsetWidth) / 2;
  };

  medias.forEach((media) => {
    const cards = [...media.querySelectorAll('.cs__card')];
    if (!cards.length) return;
    const animated = media.classList.contains('cs__media--anim');

    let spacing = 0;
    let cfRaf = null;   // coverflow rAF handle
    let spRaf = null;   // spread rAF handle
    let active = false; // coverflow is driving transforms (.cs-live)
    let engaged = false; // user has taken control of the carousel → don't auto-recentre

    const measureSpacing = () =>
      (spacing = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0);

    // ---- coverflow: each card's transform from its live distance to the container centre ----
    const applyCoverflow = () => {
      cfRaf = null;
      if (!active || !mqMobile.matches) return;
      if (!spacing) measureSpacing();
      if (!spacing) return;
      const centre = media.scrollLeft + media.clientWidth / 2;
      cards.forEach((c) => {
        const slots = (c.offsetLeft + c.offsetWidth / 2 - centre) / spacing;
        const rot = slots * MAX_ROT;
        const drop = Math.abs(slots) * DROP;
        c.style.transform = `translateY(${drop.toFixed(1)}px) rotate(${rot.toFixed(2)}deg)`;
      });
    };
    const onCfScroll = () => {
      if (cfRaf == null) cfRaf = requestAnimationFrame(applyCoverflow);
    };

    // Switch from the non-scrollable spread state to the interactive coverflow in one task
    // (add .cs-live + measure + centre + apply) so the browser paints a single frame — no
    // intermediate flash. At full spread the middle is already centred and the sides already
    // sit at the arch, so the coverflow's centred layout matches → the hand-off has no jump.
    const enableCoverflow = () => {
      if (active || !mqMobile.matches) return;
      active = true;
      media.classList.add('cs-live');
      measureSpacing();
      cards.forEach((c) => (c.style.transition = 'none')); // instant response while swiping
      centreMiddle(media);
      applyCoverflow();
    };
    const disableCoverflow = () => {
      active = false;
      media.classList.remove('cs-live');
      cards.forEach((c) => {
        c.style.transform = ''; // fall back to the CSS --cs-spread formula
        c.style.transition = '';
      });
    };
    media.addEventListener('scroll', onCfScroll, { passive: true });

    // ---- scroll-linked spread (animated instances only) ----
    // transit progress: 0 as the media enters from the viewport bottom → 1 as it leaves the top
    const spreadNow = () => {
      const r = media.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const p = clamp01((vh - r.top) / (vh + r.height));
      const s = mqMobile.matches
        ? clamp01(p / PEAK)      // mobile: rise then HOLD at widest
        : Math.sin(Math.PI * p); // desktop: symmetric, reverses on the way out
      // hand off to the coverflow only once the spread has fully finished (widest arch), so the
      // horizontal swipe is locked until the animation completes and the hand-off has no jump
      const done = mqMobile.matches && s >= 0.999;
      return { s, done };
    };
    const updateSpread = () => {
      spRaf = null;
      if (!animated || active || mqReduce.matches) return; // coverflow / reduced-motion own it
      const { s, done } = spreadNow();
      media.style.setProperty('--cs-spread', s.toFixed(3));
      if (done) enableCoverflow();
    };
    const onWinScroll = () => {
      if (spRaf == null) spRaf = requestAnimationFrame(updateSpread);
    };

    // ---- boot ----
    if (animated && mqReduce.matches) {
      // reduced motion: no scroll linking. CSS pins the static arch; on mobile allow the swipe.
      if (mqMobile.matches) enableCoverflow();
    } else if (animated) {
      // Horizon scrolls inside .page-wrapper (html is pinned to viewport height), so the page
      // scroll event does NOT fire on window. Capture-phase on document catches the non-bubbling
      // scroll from whichever element actually scrolls (page-wrapper, or window as a fallback).
      document.addEventListener('scroll', onWinScroll, { capture: true, passive: true });
      updateSpread(); // seed the spread for the current scroll position (no first-paint pop)
    } else if (mqMobile.matches) {
      // non-animated: static arch already in CSS → coverflow available immediately
      enableCoverflow();
    }

    // ---- viewport changes ----
    let t;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (!mqMobile.matches) {
          if (active) disableCoverflow(); // back to desktop → CSS --cs-spread drives the arch
          if (animated && !mqReduce.matches) updateSpread();
          return;
        }
        // mobile
        if (active) {
          measureSpacing();
          if (!engaged) centreMiddle(media);
          applyCoverflow();
        } else if (animated && !mqReduce.matches) {
          updateSpread();
        } else {
          enableCoverflow();
        }
      }, 150);
    });

    // The instant the user grabs the carousel after hand-off, stop auto-recentring on resize.
    ['pointerdown', 'touchstart'].forEach((ev) =>
      media.addEventListener(ev, () => { if (active) engaged = true; }, { passive: true })
    );
  });
})();
